import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericButton from "@ui-components/Button/GenericButton";
import {
  formatContractDate,
  withFallback,
} from "kontrolEdge/helpers/formattersShappedData";
import { ResourceTagsTableCell } from "../Shared/ResourceTagsTableCell";
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { mapKeListResourceTags } from "../Shared/mapKeListResourceTags";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import {
  shapeCarrierAccess,
  backhaulSummary,
  type CarrierAccess,
} from "kontrolEdge/helpers/carrierAccess";
import CarrierAccessSummary from "./CarrierAccessSummary";
import {
  cpeSiteType,
  shapeSiteLocation,
  type CpeSiteType,
  type SiteLocation,
} from "./cpeSite";

/**
 * Role of a CPE in its site, decided by the backend from the router
 * hostname (RT1/RT01 is active). Null when it cannot tell — a pair where
 * neither router is unit 1, or no router yet.
 */
export type CpeRole = "active" | "standby" | null;

/** One router of an Extended PoP site: a CPE, 80-XXXXXX / 83-XXXXXX. */
export interface ExtenderPopCpe {
  id: number;
  serviceId: string;
  name: string;
  status: string;
  role: CpeRole;
  /** The CPE carries the subscription: one per Odoo line. */
  term: string;
  renewDate: string;
  renewDateRaw: Date | null;
}

/** A CPE site (85-), "Extended PoP" in the code, with its routers. */
export interface ExtenderPop extends SiteLocation {
  id: number;
  /** The CPE's one id (80-XXXXXX); its routers derive theirs from it. */
  serviceId: string;
  name: string;
  /** HA with two routers or more, else Standalone. */
  type: CpeSiteType;
  /** Rich tags from the Tags Manager pivot (§5.5). */
  tagsRich?: ResourceTagSummary[];
  /** Lifecycle: draft | provisioning | active | suspended | decommissioned. */
  status: string;
  /**
   * Carriers reaching the site's routers; an Extended PoP is always reached
   * by carrier access (one or more), never by a cross connect.
   */
  carrierAccess: CarrierAccess[];
  /** The site's routers, active first. */
  cpes: ExtenderPopCpe[];
  /** Term of the active CPE, the one the list shows. */
  term: string;
  renewDate: string;
  renewDateRaw: Date | null;
  customer?: {
    name: string;
    chargebeeCustomerId: string;
  };
}

interface RolePresentation {
  variant: "success" | "default";
  label: string;
}

const ROLE_PRESENTATION: Record<"active" | "standby", RolePresentation> = {
  active: { variant: "success", label: "Active" },
  standby: { variant: "default", label: "Hot-Standby" },
};

/** Pill for a CPE role; null when the role is unknown. */
export const cpeRolePresentation = (
  role: CpeRole | undefined
): RolePresentation | null => (role ? ROLE_PRESENTATION[role] : null);

/** Where a CPE id leads: its own detail, under its site. */
export const cpeDetailPath = (extenderPopId: number, cpeId: number): string =>
  buildDetailPath("extenderPopCpe", { extenderPopId, cpeId });

/**
 * The street address the customer gave, or "-". The GPS position is not
 * shown here: it lives in the CPE detail, next to the address it locates.
 */
const AddressCell: React.FC<{ row: ExtenderPop }> = ({ row }) => (
  <div>{row.address ?? "-"}</div>
);

interface StatusPresentation {
  variant: "success" | "default" | "error" | "warning";
  label: string;
  rank: number;
}

/** Single source for the Status pill and its sort order. */
const STATUS_PRESENTATION: Record<string, StatusPresentation> = {
  active: { variant: "success", label: "Active", rank: 5 },
  provisioning: { variant: "warning", label: "Provisioning", rank: 4 },
  draft: { variant: "default", label: "Draft", rank: 3 },
  suspended: { variant: "warning", label: "Suspended", rank: 2 },
  decommissioned: { variant: "error", label: "Decommissioned", rank: 1 },
};

export const extenderPopStatusPresentation = (
  status?: string | null
): StatusPresentation =>
  STATUS_PRESENTATION[status ?? ""] ?? {
    variant: "default",
    label: "Unknown",
    rank: 0,
  };

export const extenderPopColumns: GenericTableColumn<ExtenderPop>[] = [
  {
    header: "CPE ID",
    accessor: "serviceId",
    renderCell: (row) => <GenericButton variant="brand" label={row.serviceId} />,
  },
  {
    header: "Name",
    accessor: "name",
  },
  {
    header: "Location Name",
    renderCell: (row) => <div>{row.locationName ?? "-"}</div>,
    sortAccessor: (row) => row.locationName ?? "",
  },
  {
    header: "Address",
    renderCell: (row) => <AddressCell row={row} />,
    sortAccessor: (row) => row.address ?? "",
  },
  {
    header: "Status",
    renderCell: (row) => {
      const { variant, label } = extenderPopStatusPresentation(row.status);
      return <GenericTag variant={variant} label={label} size="small" />;
    },
    sortAccessor: (row) => extenderPopStatusPresentation(row.status).rank,
  },
  {
    header: "Tags",
    renderCell: (row) => (
      <ResourceTagsTableCell
        tagsRich={row.tagsRich}
        maxVisible={1}
        triggerSingleRow
      />
    ),
  },
  {
    header: "Type",
    renderCell: (row) => <div>{row.type}</div>,
    sortAccessor: (row) => row.type,
  },
  {
    // Carrier Access, shown as "Backhaul": one line per sold access, like
    // the Ports list ("84-C54988 · AXTEL, IENTC"). Redundancy and priority
    // live on the CPE and Backhaul details.
    header: "Backhaul",
    renderCell: (row) => <CarrierAccessSummary carriers={row.carrierAccess} compact />,
    sortAccessor: (row) =>
      backhaulSummary(row.carrierAccess)
        .map((group) => group.access?.serviceId ?? "~")
        .join(" "),
  },
  {
    header: "Term",
    renderCell: (row) => <div>{row.term}</div>,
  },
  {
    header: "Renew Date",
    accessor: "renewDate",
  },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const shapeTerm = (subscription: any) => {
  const renewIso =
    subscription?.renewDate && subscription.renewDate !== "N/A"
      ? subscription.renewDate
      : null;
  const rawDate = renewIso ? new Date(renewIso) : null;
  return {
    term: subscription?.term
      ? `${subscription.term} ${withFallback(subscription.term_unit)}`
      : "-",
    renewDate: renewIso ? formatContractDate(renewIso) : "-",
    renewDateRaw: rawDate && !isNaN(rawDate.getTime()) ? rawDate : null,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const shapeExtenderPopData = (rows: any[]): ExtenderPop[] => {
  return rows.map((r) => {
    const customer = r.customer
      ? {
          name: r.customer.name ?? "-",
          chargebeeCustomerId: r.customer.chargebeeCustomerId ?? "-",
        }
      : undefined;

    // The backend serves the CPEs active first.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cpes: ExtenderPopCpe[] = (r.cpes ?? []).map((cpe: any) => ({
      id: cpe.id,
      serviceId: withFallback(cpe.serviceId),
      name: withFallback(cpe.name),
      status: cpe.status ?? "unknown",
      role: cpe.role ?? null,
      ...shapeTerm(cpe.subscription),
    }));
    const lead = cpes[0];

    return {
      id: r.id,
      serviceId: withFallback(r.serviceId),
      name: withFallback(r.name),
      ...shapeSiteLocation(r),
      type: cpeSiteType(cpes.length),
      tagsRich: mapKeListResourceTags(r),
      status: r.status ?? "unknown",
      carrierAccess: shapeCarrierAccess(r),
      cpes,
      term: lead?.term ?? "-",
      renewDate: lead?.renewDate ?? "-",
      renewDateRaw: lead?.renewDateRaw ?? null,
      customer,
    };
  });
};
